import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import RootLayout from "./components/RootLayout";
import PremisesPage from "./pages/PremisesPage";
import CompaniesPage from "./pages/CompaniesPage";
import DemoStudioPage from "./pages/DemoStudioPage";
import RoomTypesPage from "./pages/RoomTypesPage";
import RadioStationsPage from "./pages/RadioStationsPage";
import AppCatalogPage from "./pages/AppCatalogPage";
import GlobalTemplatesPage from "./pages/GlobalTemplatesPage";
import GlobalMediaPage from "./pages/GlobalMediaPage";
import DistributionPage from "./pages/DistributionPage";
import LoginPage from "./pages/LoginPage";
import UsersPage from "./pages/UsersPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ContentAnalyticsPage from "./pages/ContentAnalyticsPage";
import OtaPage from "./pages/OtaPage";
import DeviceGroupsPage from "./pages/DeviceGroupsPage";
import HealthAlertsPage from "./pages/HealthAlertsPage";
import MeteredUsagePage from "./pages/MeteredUsagePage";
import ClientErrorsPage from "./pages/ClientErrorsPage";
import GeneralSettingsPage from "./pages/GeneralSettingsPage";
import SignageOverviewPage from "./pages/SignageOverviewPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ScopeProvider } from "./context/ScopeContext";
import { PremiseWorkspaceProvider } from "./context/PremiseWorkspace";
import WorkspacePage from "./pages/WorkspacePage";
import PremiseRedirect from "./pages/PremiseRedirect";
import RequireAuth from "./auth/RequireAuth";
import RequireStaff from "./auth/RequireStaff";
import OverviewRedirect from "./pages/OverviewRedirect";
import SupplierShell from "./components/SupplierShell";
import SupplierTitlesPage from "./pages/supplier/SupplierTitlesPage";
import SupplierTitlePage from "./pages/supplier/SupplierTitlePage";
import SupplierBrandPage from "./pages/supplier/SupplierBrandPage";
import SupplierUploadPage from "./pages/supplier/SupplierUploadPage";
import SupplierRevenuePage from "./pages/supplier/SupplierRevenuePage";
import SupplierAudiencePage from "./pages/supplier/SupplierAudiencePage";
import SupplierAgreementsPage from "./pages/supplier/SupplierAgreementsPage";
import SupplierCustomersPage from "./pages/supplier/SupplierCustomersPage";
import SupplierTeamPage from "./pages/supplier/SupplierTeamPage";
import { isContentOwnerSession, landingPathFor } from "./auth/audience";

/**
 * Landing page. Fleet health is the default home, but it needs `canViewFleet`
 * - a user without it (a premise editor, say) would land on a page the API
 * refuses, so they keep the old Premises home. A content owner lands in their
 * own shell, which is a different building entirely (`CONTENT-4`).
 */
function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={landingPathFor(user)} replace />;
}

/**
 * The operator CMS, closed to content owners (`CONTENT-4`).
 *
 * Redirect rather than a bare refusal: an external party typing `/fleet` has
 * made a mistake, not an attack, and sending them home is both kinder and
 * quieter than a page that names a surface they cannot have. The refusal that
 * MATTERS is the server's - every one of these pages is gated there too.
 */
function StaffOnly({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (isContentOwnerSession(user)) {
    return <Navigate to="/supplier/titles" replace />;
  }
  return <>{children}</>;
}

/** The supplier shell, closed to everybody else - see {@link StaffOnly}. */
function OwnerOnly({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (!isContentOwnerSession(user)) {
    return <Navigate to={landingPathFor(user)} replace />;
  }
  return <>{children}</>;
}

// Vite injects BASE_URL based on the config's `base` option. In dev the CMS
// runs at "/", in production at "/cms/". React Router needs to know which
// so internal navigation rewrites correctly.
const ROUTER_BASENAME =
  (import.meta.env.BASE_URL ?? "/").replace(/\/+$/, "") || undefined;

export default function App() {
  return (
    <BrowserRouter basename={ROUTER_BASENAME}>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
          {/* The supplier shell. Same auth, same build - a different
              building. Registered before the operator routes so its own
              layout, not RootLayout, is what a content owner ever sees. */}
          <Route
            element={
              <RequireAuth>
                <OwnerOnly>
                  <SupplierShell />
                </OwnerOnly>
              </RequireAuth>
            }
          >
            <Route
              path="/supplier"
              element={<Navigate to="/supplier/titles" replace />}
            />
            <Route path="/supplier/titles" element={<SupplierTitlesPage />} />
            <Route
              path="/supplier/titles/:assetId"
              element={<SupplierTitlePage />}
            />
            <Route
              path="/supplier/customers"
              element={<SupplierCustomersPage />}
            />
            <Route path="/supplier/upload" element={<SupplierUploadPage />} />
            <Route
              path="/supplier/audience"
              element={<SupplierAudiencePage />}
            />
            <Route path="/supplier/revenue" element={<SupplierRevenuePage />} />
            <Route
              path="/supplier/agreements"
              element={<SupplierAgreementsPage />}
            />
            <Route path="/supplier/brand" element={<SupplierBrandPage />} />
            <Route path="/supplier/team" element={<SupplierTeamPage />} />
          </Route>

          <Route
            element={
              <RequireAuth>
                <StaffOnly>
                  {/* Above RootLayout: the sidebar itself reads the scope. */}
                  <ScopeProvider>
                    <PremiseWorkspaceProvider>
                      <RootLayout />
                    </PremiseWorkspaceProvider>
                  </ScopeProvider>
                </StaffOnly>
              </RequireAuth>
            }
          >
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/premises" element={<PremisesPage />} />
            {/* Opening a premise scopes the header to it and hands off to the
                product areas - there is no separate premise editor any more. */}
            <Route path="/premises/:premiseId" element={<PremiseRedirect />} />

            {/* Product areas. The section is a route param; the premise comes
                from the header scope. */}
            <Route
              path="/places/:section"
              element={<WorkspacePage area="Location" />}
            />
            <Route
              path="/frontlayer/:section"
              element={<WorkspacePage area="Frontlayer" />}
            />
            {/* One level deeper: Home screen → Information. */}
            <Route
              path="/frontlayer/:section/:sub"
              element={<WorkspacePage area="Frontlayer" />}
            />
            <Route
              path="/guest-web"
              element={<WorkspacePage area="Guest web" section="guest-web" />}
            />
            <Route
              path="/promotions/:section"
              element={<WorkspacePage area="Promotions" />}
            />
            <Route
              path="/care/:section"
              element={<WorkspacePage area="Care" />}
            />
            <Route
              path="/video-calling/:section"
              element={<WorkspacePage area="Video calling" />}
            />
            <Route
              path="/signage/:section"
              element={<WorkspacePage area="Signage" />}
            />
            <Route
              path="/streaming-tv/:section"
              element={<WorkspacePage area="Streaming TV" />}
            />
            {/* Nenda-staff pages: guarded here as well as hidden in the menu,
                because the URL bar is a second way in. Keep each gate in step
                with its nav item in RootLayout. */}
            <Route element={<RequireStaff />}>
              <Route path="/companies" element={<CompaniesPage />} />
              <Route path="/demo-studio" element={<DemoStudioPage />} />
              <Route
                path="/demo-studio/:sessionId"
                element={<DemoStudioPage />}
              />
              <Route path="/room-types" element={<RoomTypesPage />} />
            </Route>
            {/* The standalone Fleet overview was merged into the analytics
                page; keep the old path working (deep-links carry ?status=). */}
            <Route
              path="/fleet"
              element={<Navigate to="/analytics" replace />}
            />
            <Route path="/analytics" element={<AnalyticsPage />} />
            {/* A customer's landing: resolves to their Frontlayer or Signage
                overview by the header's product selector. */}
            <Route path="/overview" element={<OverviewRedirect />} />
            {/* Chain-level CONTENT analytics (what guests use), distinct from
                /analytics which is the device fleet's health. */}
            <Route
              path="/content-analytics"
              element={<ContentAnalyticsPage />}
            />
            <Route path="/ota" element={<OtaPage />} />
            {/* The signage estate view. A fleet-wide page like its siblings
                above, scoped from the header bar rather than from the URL. */}
            <Route path="/signage-overview" element={<SignageOverviewPage />} />
            <Route path="/device-groups" element={<DeviceGroupsPage />} />
            <Route path="/health-alerts" element={<HealthAlertsPage />} />
            <Route path="/metered-usage" element={<MeteredUsagePage />} />
            <Route element={<RequireStaff permission="canManageSettings" />}>
              <Route path="/client-errors" element={<ClientErrorsPage />} />
              <Route
                path="/settings/general"
                element={<GeneralSettingsPage />}
              />
            </Route>
            <Route
              element={<RequireStaff permission="canManageSchemeDefaults" />}
            >
              <Route path="/templates" element={<GlobalTemplatesPage />} />
            </Route>
            <Route
              element={<RequireStaff permission="canManageRadioStations" />}
            >
              <Route path="/radio-stations" element={<RadioStationsPage />} />
            </Route>
            <Route element={<RequireStaff permission="canManageApps" />}>
              <Route path="/apps" element={<AppCatalogPage />} />
            </Route>
            <Route element={<RequireStaff permission="canManageMedia" />}>
              <Route path="/media" element={<GlobalMediaPage />} />
            </Route>
            {/* Platform-level distribution. One route, four sections - each
                becomes a real editor by replacing its branch. */}
            <Route
              path="/distribution"
              element={<Navigate to="/distribution/channels" replace />}
            />
            <Route
              element={<RequireStaff permission="canManageDistribution" />}
            >
              <Route
                path="/distribution/:section"
                element={<DistributionPage />}
              />
            </Route>
            <Route element={<RequireStaff permission="canManageUsers" />}>
              <Route path="/users" element={<UsersPage />} />
            </Route>
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
